import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CKEditorComponent from '../../components/CKEditorComponent';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import MultiSelect from '../../components/MultiSelect';
import FileUpload from '../../components/FileUpload';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import blogApi from '../../api/blogApi';
import blogCategoryApi from '../../api/blogCategoryApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const AddBlog = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    description: '',
    categories: [],
    image: '',
    is_active: 1,
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await blogCategoryApi.getBlogCategories({ paginate: false, includeTrashed: false });
        setAvailableCategories(response.data.map(cat => ({ value: cat.id, label: cat.name })));
      } catch (err) {
        console.error('Failed to fetch categories for MultiSelect:', err);
        addToast('Failed to load categories for selection.', 'error');
      }
    };
    fetchCategories();
  }, [addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  const handleCategoryChange = (selectedOptions) => {
    setFormData({ ...formData, categories: selectedOptions ? selectedOptions.map(option => option.value) : [] });
    if (validationErrors.categories) {
      setValidationErrors(prev => ({ ...prev, categories: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'categories') {
        formData[key].forEach(catId => data.append('categories[]', catId));
      } else if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else {
        data.append(key, formData[key]);
      }
    }

    try {
      await blogApi.createBlog(data);
      addToast('Blog created successfully!', 'success');
      navigate(ROUTES.BLOGS);
    } catch (err) {
      console.error('Failed to add blog:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to add blog. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Add New Blog</h1>
      {loading ? (
        <SkeletonLoader type="form" />
      ) : (
        <form onSubmit={handleSubmit}>
          <TextInput
            label="Blog Title"
            name="title"
            id="title"
            value={formData.title}
            onChange={handleChange}
            required={true}
            placeholder="Blog Title"
            error={validationErrors?.title?.[0]}
          />
          <TextAreaInput
            label="Excerpt"
            name="excerpt"
            id="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            required={true}
            rows={3}
            placeholder="Excerpt"
            error={validationErrors?.excerpt?.[0]}
          />
          
          <CKEditorComponent
            label="Description"
            value={formData.description}
            onChange={handleDescriptionChange}
            required={true}
            error={validationErrors?.description?.[0]}
          />

          <MultiSelect
            label="Blog Categories"
            options={availableCategories}
            value={formData.categories.map(catId => ({ value: catId, label: availableCategories.find(c => c.value === catId)?.label || catId }))}
            onChange={handleCategoryChange}
            placeholder="Select categories..."
            error={validationErrors?.categories?.[0]}
            required={true}
          />
          <FileUpload
            label="Blog Image"
            name="image"
            id="image"
            onChange={handleChange}
            error={validationErrors?.image?.[0]}
            required={false}
            currentFileUrl={null}
            onRemoveFile={null}
            fileType="image"
            fileName="Image"
            fallbackName={formData.title || 'Blog Image'}
          />
          <CheckboxInput
            label="Active"
            name="is_active"
            id="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <div className="flex items-center justify-between mt-6">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Add Blog
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/blogs')}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddBlog;